import { HttpClient, toQueryString } from '@emobg/web-utils';

import type { Country } from '@store/Broker/interfaces';

import { isLocalApi } from '../plugins/api/api';
import { API_ENDPOINTS, baseEndpointUrl } from './api.const';
import type {
  AddressPayload,
  AddressResponse,
  CheckpointData,
  CitiesResponse,
  ClientDataResponse,
  OnfidoTokenResponse,
  ProvincesResponse,
  ScannedDocumentPayload,
  UpdateInitialDataPayload,
  UpdateInitialDataResponse,
  ValidateBookingPayload,
  ValidateBookingResponse,
} from './api.model';

export class ApiService {
  /**
   * Retrieves all user related data available
   */
  async getUpdateInitialData(payload: UpdateInitialDataPayload): Promise<UpdateInitialDataResponse> {
    const url = API_ENDPOINTS.getUpdateInitialData(payload.selectedCountryCode || '');
    const { headers } = payload;

    const httpClient = new HttpClient({
      baseURL: baseEndpointUrl,
      rawError: true,
    });

    const data = await httpClient.get<UpdateInitialDataResponse>(url, { headers });
    return data;
  }

  /**
   * Validates booking data
   * @param bookingId `string`
   * @param bookingDate `string`
   * @param email `string`
   * @param locale `string`
   */
  async getValidateBooking(payload: ValidateBookingPayload): Promise<ValidateBookingResponse> {
    const { bookingId, email, bookingDate, locale } = payload;

    const httpClient = new HttpClient({
      baseURL: baseEndpointUrl,
      rawError: true,
    });

    const url = API_ENDPOINTS.getValidateBooking
      .replace('{{bookingId}}', bookingId)
      .replace('{{bookingDate}}', bookingDate)
      .replace('{{email}}', email)
      .replace('{{locale}}', locale);

    const promise = httpClient.get<ValidateBookingResponse>(url);

    return promise.then((data) => data).catch((error) => error.body);
  }

  /**
   * Gets customer address data
   * @param headers `HeadersInit`
   */
  async getAddress(headers: HeadersInit): Promise<AddressResponse> {
    const httpClient = new HttpClient({
      baseURL: baseEndpointUrl,
      rawError: true,
    });
    const data = await httpClient.get<AddressResponse>(API_ENDPOINTS.getAddress, { headers });
    return data;
  }

  /**
   * Gets an array of provinces
   * @param countryCode `string`
   * @returns `Promise<ProvincesResponse>`
   */
  async getProvinces(countryCode: string): Promise<ProvincesResponse> {
    const url = API_ENDPOINTS.getProvinces.replace('{{countryCode}}', countryCode);
    const httpClient = new HttpClient({
      baseURL: baseEndpointUrl,
      rawError: true,
    });
    const data = await httpClient.get<ProvincesResponse>(url);

    return data;
  }

  /**
   * Gets an array of cities
   * @param countryCode `string`
   * @param search `string`
   * @returns `Promise<ProvincesResponse>`
   */
  async getCities(countryCode: string, search: string): Promise<CitiesResponse> {
    const pattern = search.trim();
    const httpClient = new HttpClient({
      baseURL: baseEndpointUrl,
      rawError: true,
    });

    // eslint-disable-next-line id-length
    const query = toQueryString({ countryCode, q: pattern }, true, true);
    const url = `${API_ENDPOINTS.getCities}?${query}`;

    const data = await httpClient.get<CitiesResponse>(url);
    return data;
  }

  /**
   * Gets all data from a client
   * @param brokerId `string`
   * @param brokerToken `string`
   * @returns `Promise<ClientDataResponse>`
   */
  async getClientData(brokerId: string, brokerToken: string): Promise<ClientDataResponse> {
    const httpClient = new HttpClient({
      baseURL: baseEndpointUrl,
      rawError: true,
    });
    const data = await httpClient.get<ClientDataResponse>(API_ENDPOINTS.getClientData, {
      headers: {
        'X-Broker-Id': brokerId,
        'X-Broker-Token': brokerToken,
      },
    });

    return data;
  }

  /**
   * Gets Onfido token and applicant ID
   * @param brokerId `string`
   * @param brokerToken `string`
   * @param referer `string`
   * @returns `OnfidoTokenResponse`
   */
  async postOnfidoData(brokerId: string, brokerToken: string): Promise<OnfidoTokenResponse> {
    const xReferer = isLocalApi ? process.env.VUE_APP_LOCAL : location.origin;
    const options = {
      headers: {
        'X-Broker-Id': brokerId,
        'X-Broker-Token': brokerToken,
        'X-Referer': xReferer,
      } as Record<string, string>,
    } as const;
    const httpClient = new HttpClient({
      baseURL: baseEndpointUrl,
      rawError: true,
    });

    const data = await httpClient.post<OnfidoTokenResponse, null>(API_ENDPOINTS.postOnfidoData, null, options);

    return data;
  }

  /**
   * Posts scanned document and applicant data
   * @param headers `HeadersInit`
   * @param scannedDocument `ScannedDocumentPayload`
   */
  async postScannedDocument(headers: HeadersInit, scannedDocument: ScannedDocumentPayload): Promise<{ checkId: string }> {
    const httpClient = new HttpClient({
      baseURL: baseEndpointUrl,
      rawError: true,
    });
    const data = await httpClient.post<{ checkId: string }, ScannedDocumentPayload>(API_ENDPOINTS.postScannedDocument, scannedDocument, { headers });

    return data;
  }

  /**
   * Post data about the current step
   * @param brokerId `string`
   * @param brokerToken `string`
   * @param checkpointData `Partial<CheckpointData>`
   */
  postCheckpoint(brokerId: string, brokerToken: string, checkpointData: Partial<CheckpointData>) {
    const httpClient = new HttpClient({
      baseURL: baseEndpointUrl,
      rawError: true,
    });
    httpClient.post(
      API_ENDPOINTS.postCheckpoint,
      { ...checkpointData, utcTime: Date.now() },
      {
        headers: {
          'Content-Type': 'application/json',
          'X-Broker-Id': brokerId,
          'X-Broker-Token': brokerToken,
        },
      },
    );
  }

  /**
   * Updates address data
   * @param headers `HeadersInit`
   * @param addressPayload `AddressPayload`
   */
  async putUpdateAddress(headers: HeadersInit, addressPayload: AddressPayload): Promise<AddressResponse> {
    const httpClient = new HttpClient({
      baseURL: baseEndpointUrl,
      rawError: true,
    });
    const data = await httpClient.put<AddressResponse, AddressPayload>(API_ENDPOINTS.putUpdateAddress, addressPayload, { headers });

    return data;
  }

  /**
   * Updates client data
   * @param headers `HeadersInit`
   * @param clientData `Partial<ClientDataResponse>`
   */
  async putClientData(headers: HeadersInit, clientData: Partial<ClientDataResponse>) {
    const httpClient = new HttpClient({
      baseURL: baseEndpointUrl,
      rawError: true,
    });
    const data = await httpClient.put(API_ENDPOINTS.putClientData, clientData, { headers });
    return data;
  }

  /**
   * Sends documents checks ids previously gathered from Onfido
   * @param headers `HeadersInit`
   * @param documentsChecksIds `string[]`
   */
  async putDocumentsChecksIds(headers: HeadersInit, documentsChecksIds: string[]): Promise<{ status: number }> {
    const httpClient = new HttpClient({
      baseURL: baseEndpointUrl,
      rawError: true,
    });
    return httpClient.put(API_ENDPOINTS.putDocumentsChecksIds, documentsChecksIds, { headers });
  }

  /**
   * Gets a list of countries
   * @param locale `string`
   */
  async getCountries(locale: string): Promise<{ countries: Country[] }> {
    const httpClient = new HttpClient({
      baseURL: baseEndpointUrl,
      rawError: true,
    });
    const data = await httpClient.get<{ countries: Country[] }>(API_ENDPOINTS.countries(locale));
    return data;
  }
}
